import { config } from "../config/config";
import { openNotificationWithIcon } from "./toast";

export const asbestos_type_options = [
  "Amosite",
  "Chrysotile",
  "Crocidolite",
  "Other",
];
export const other_fibrous_options = [
  "Fiberglass",
  "Mineral Wool",
  "Cellulose",
  "Ceramic",
  "Synthetic Fibers",
  "other",
];
export const non_fibrous_options = [
  "Carbonaceous",
  "Non-Carbonaceous",
  "Ceramic",
  "Tile",
  "Tar",
  "Vinyl",
  "Filter, Binder",
  "Perlite",
  "Plaster",
  "Vermiculite",
  "Paint",
  "Clay",
  "Quartz",
  "Urethane",
  "Foam Glass",
  "Mastic",
  "Gypsum",
  "Ca Carbonate",
  "Glass",
  "Matrix",
  "Sealant",
  "Foam",
  "Brick",
  "Mortar",
  "Formica",
  "Foil",
  "Latex",
  "Other",
];

// export const COMMON_COLORS_OPTIONS = [
//   "Black",
//   "Brown",
//   "Clear",
//   "Gray",
//   "Silver",
//   "Tan",
//   "White",
//   "Yellow",
//   "Other",
// ]

export function getFirstPathSegment(path) {
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;

  const segments = normalizedPath.split("/");

  return segments[0];
}

export const buildingValidationObj = {
  BUILDING_ID: "BUILDING ID",
  DEMOLISHED: "DEMOLISHED",
  CERTIFIED: "CERTIFIED",
  NO_LEVELS: "LEVELS",
  BUILDING_SIZE: "BUILDING SIZE",
};

export const surveyValidationObj = {
  NO_LEVELS: "LEVELS",
  BUILDING_SIZE: "SIZE",
  DESCRIPTION: "DESCRIPTION",
  FULL_BLDG_SURVEY: "FULL BUILDING SURVEY",
  FIRE_PROTECTION: "FIRE PROTECTION",
  COMMENTS: "COMMENTS",
  STRUCTURE_TYPE: "STRUCTURE TYPE",
  EXTERIOR_TYPE: "EXTERIOR TYPE",
  ROOF_TYPE: "ROOF TYPE",
  HEATING_TYPE: "HEATING TYPE ",
  COOL_TYPE: "COOL TYPE",
  PLUMBING_TYPE: "PLUMBING TYPE",
  INACCESS_AREA: "INACCESS AREA",
  INACCESS_AREA_DESC: "INACCESS AREA DESC",
  INACCESS_AREA_ACM: "INACCESS AREA ACM",
  INACCESS_AREA_ACM_QTY: "INACCESS AREA ACM QTY",
  INACCESS_AREA_REASON: "INACCESS AREA REASON",
};

export const sampleValidationObj = {
  SAMPLE_ID: "SAMPLE NAME",
  SAMPLE_DATE: "SAMPLE DATE",
  LBTRY_ID: "LABORATORY",
  COMMENTS: "COMMENTS",
};

export const resultValidationObj = {
  ASB_PRESENT: "ASBESTOS PRESENT",
  LEAD_PRESENT: "LEAD PRESENT",
  HOMOG: "HOMOGENEOUS",
  FPLA_ID: "FPLA ID ",
  RESULT_DATE: "RESULT DATE",
  TECH_ID: "TECH ID",
  LAYER_ID: "LAYER ID",
  CATEGORY: "TYPE",
  DIST_FEATURE: "MORPHOLOGY",
  DIST_FEATURE: "DIST FEATURE",
  RES_TYPE: "RESULT TYPE",
  ASB_MATERIAL: "ASBESTOS FOUND IN MATERIAL",
};

/**
 * BUILDING_SURVEY.INSPECTOR holds two formats. Rows written by this app are a
 * JSON array (["RM","JH"]). Rows carried over from the legacy 1997 system are a
 * "*"-separated list of inspector initials ("CC*RM*JH"), which is not JSON.
 *
 * JSON.parse on a legacy value throws, and the caller's catch reports
 * error?.response?.data?.message -- undefined for a SyntaxError -- so the survey
 * silently rendered empty with a blank error toast. 1,092 of 1,185 surveys in
 * production (92%) are in the legacy format.
 */
export function parseInspectors(value) {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value;

  const raw = String(value).trim();
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    return [String(parsed)];
  } catch {
    // Legacy "*"-separated initials
    return raw
      .split("*")
      .map((s) => s.trim())
      .filter(Boolean);
  }
}

export const checkEmpty = function (item) {
  if (item === "" || item === undefined || item === null) {
    return true;
  } else {
    return false;
  }
};

/**
 * For the result edit form. Legacy rows keep the real percentage in PERCENT1
 * with a dummy PERCENT_RANGE of "0", and the form's percent input binds
 * PERCENT_RANGE -- so it showed "0" for a result the view page shows as 2%.
 * That mismatch invites a "correction" that overwrites the real value. Show the
 * real value instead. Local display state only: nothing is sent unless edited.
 */
export function withDisplayPercent(results) {
  if (!Array.isArray(results)) return results;
  return results.map((r) => {
    if (!r) return r;
    const range = String(r.PERCENT_RANGE ?? "").trim().replace(/%/g, "");
    const p1 = Number(r.PERCENT1);
    if (range === "0" && r.PERCENT1 !== null && r.PERCENT1 !== "" && !Number.isNaN(p1) && p1 !== 0) {
      return { ...r, PERCENT_RANGE: String(r.PERCENT1) };
    }
    return r;
  });
}

/**
 * Fix numeric % before create/update: UI sets PERCENT=1 as a mode flag while
 * the real value is in PERCENT_RANGE. Map that value onto PERCENT/PERCENT1.
 */
export function prepareResultMeasurePayload(details) {
  if (!details || typeof details !== "object") return details;
  const payload = { ...details };
  const percentRange = payload.PERCENT_RANGE;
  const isNumeric =
    percentRange !== "" &&
    percentRange !== undefined &&
    percentRange !== null &&
    !String(percentRange).includes("-") &&
    !Number.isNaN(Number(String(percentRange).trim()));

  // A PERCENT_RANGE of "0" is legacy backfill meaning "no value", not a real 0%
  // reading. Deriving PERCENT/PERCENT1 from it overwrites the true percentage that
  // legacy rows keep in PERCENT1. Matches the backend guard in ccf3d74.
  const numericValue = isNumeric ? Number(String(percentRange).trim()) : null;

  if (isNumeric && numericValue !== 0) {
    payload.PERCENT = numericValue;
    payload.PERCENT1 = numericValue;
  } else if (
    (payload.RANGE || payload.TRACE) &&
    (payload.PERCENT === 1 || payload.PERCENT === "1")
  ) {
    delete payload.PERCENT;
  }

  if (payload.RANGE !== undefined && payload.RANGE1 === undefined) {
    payload.RANGE1 = payload.RANGE;
  }

  return payload;
}

// export const downloadFile = function (file, url, fileName) {
//   const token = localStorage.getItem("token");

//   fetch(`${config.API_BASE_URL}${url}`, {
//     method: "GET",
//     headers: {
//       Authorization: `Bearer ${token}`,
//       "Content-Type": "application/pdf",
//     },
//     responseType: "arraybuffer",
//   })
//     .then((res) => res.blob())
//     .then((blob) => {
//       const url = window.URL.createObjectURL(blob);
//       const link = document.createElement("a");
//       link.href = url;
//       link.download = `${fileName}.${file}`;
//       link.click();
//       window.URL.revokeObjectURL(url);
//     })
//     .catch((error) => {
//       console.error(`Error fetching PDF:`, error);
//     });
// };
export const downloadFile = async function (file, url, fileName) {
  const token = localStorage.getItem("token");

  try {
    const res = await fetch(`${config.API_BASE_URL}${url}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/pdf",
      },
    });

    if (!res.ok) {
      throw new Error("Failed to fetch file");
    }

    const blob = await res.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = `${fileName}.${file}`;
    link.click();
    window.URL.revokeObjectURL(objectUrl);
  } catch (error) {
    // Previously swallowed: a 401/403/500 produced no toast, no spinner change
    // and no file, so clicking Download appeared to do nothing at all.
    console.error(`Error fetching file:`, error);
    openNotificationWithIcon("error", "Download failed. Please try again.");
  }
};

export const downloadFileTable = async function (file, url, fileName, data) {
  const token = localStorage.getItem("token");

  try {
    const res = await fetch(`${config.API_BASE_URL}${url}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!res.ok) {
      throw new Error("Failed to fetch file");
    }

    const blob = await res.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = `${fileName}.${file}`;
    link.click();
    window.URL.revokeObjectURL(objectUrl);
  } catch (error) {
    // Previously swallowed: a 401/403/500 produced no toast, no spinner change
    // and no file, so clicking Download appeared to do nothing at all.
    console.error(`Error fetching file:`, error);
    openNotificationWithIcon("error", "Download failed. Please try again.");
  }
};

export const isObjectEmpty = function (obj) {
  for (const prop in obj) {
    if (Object.hasOwn(obj, prop)) {
      return false;
    }
  }

  return true;
};

export const checkAccess = async (loginDetails, permission) => {
  // Guarded: access_rights is a free-text column. Malformed JSON used to throw
  // out of this function, which every caller awaits, turning a permission check
  // into an unhandled rejection. Failing closed to [] is the safe default --
  // the admin/SUBADMIN fallthrough below still applies.
  let rights = [];
  try {
    const parsed = JSON.parse(loginDetails?.access_rights || "[]");
    if (Array.isArray(parsed)) rights = parsed;
  } catch (e) {
    rights = [];
  }
  // console.log(JSON.parse(loginDetails.access_rights),"rigghasa")
  if (
    (rights && rights.includes(permission)) ||
    loginDetails?.role === "admin" ||
    loginDetails?.designation == "SUBADMIN"
  ) {
    return true;
  } else {
    // const errorResponse = {
    //   statusCode: 403,
    //   error: 'Bad Request',
    //   message: 'You are not authorized to perform this action',
    //   data: { permission: false }
    // };
    return false;
    // openNotificationWithIcon("error", "You are not authorized to perform this action");
    // return h.response(errorResponse).code(400).takeover();
    // throw Config.APP_CONSTANTS.responseObject(403, 'You are not authorized to perform this action', { data: { permission: false } });
  }
};
